<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { Loader2 } from 'lucide-vue-next';
  import PrinterIcon from '@/shared/assets/icons/PrinterIcon.svg';
  import { ColoredButton, ModalDocumentInfo } from '@/shared/ui';
  import { useBranchLogo } from '@/entities/Branch';
  import { useCustomToast } from '@/shared/lib';
  import { TRAINING_CONTROL_QUALITY_RECORD } from '../lib/trainingControlQualityRecord';
  import { isTrainingControlType } from '../lib/isTrainingControlType';
  import { useViewTraining } from '../model/useViewTraining';
  import { useTrainingControlPrint } from '../model/useTrainingControlPrint';
  import TrainingControlTypeSection from './control/TrainingControlTypeSection.vue';
  import TrainingControlTitleSection from './control/TrainingControlTitleSection.vue';
  import TrainingControlSchedulingSection from './control/TrainingControlSchedulingSection.vue';
  import TrainingControlProgrammingSection from './control/TrainingControlProgrammingSection.vue';
  import TrainingControlApprovalSection from './control/TrainingControlApprovalSection.vue';
  import TrainingControlDocumentsSection from './control/TrainingControlDocumentsSection.vue';
  import TrainingControlCollaboratorsSection from './control/TrainingControlCollaboratorsSection.vue';
  import TrainingControlInstructorsSection from './control/TrainingControlInstructorsSection.vue';
  import TrainingControlOpenInstructorsSection from './control/TrainingControlOpenInstructorsSection.vue';
  import TrainingControlCertificateSection from './control/TrainingControlCertificateSection.vue';
  import TrainingControlNotesSection from './control/TrainingControlNotesSection.vue';

  const TRAINING_LIST_ROUTE_NAME = 'TreinamentosListaTreinamentos';

  type TrainingControlLayout = 'onboarding-role-change' | 'sgq-punctual' | 'sgq-homologation' | 'open';

  const router = useRouter();
  const toast = useCustomToast();
  const { loading, training } = useViewTraining();
  const { isGenerating, printTrainingControl } = useTrainingControlPrint();
  const { currentLogoUrl, handleLogoError } = useBranchLogo();
  const isSupportedTraining = computed(
    () => training.value !== null && isTrainingControlType(training.value.type)
  );

  function handlePrint() {
    if (!training.value) return;
    void printTrainingControl(training.value.id);
  }

  const layout = computed<TrainingControlLayout | null>(() => {
    if (!training.value) return null;

    switch (training.value.type) {
      case 'ONBOARDING':
      case 'ROLE_CHANGE':
        return 'onboarding-role-change';
      case 'SGQ_DOCUMENT_FOCAL':
        return 'sgq-punctual';
      case 'SGQ_DOCUMENT_HOMOLOGATION':
        return 'sgq-homologation';
      case 'OPEN_TRAINING':
        return 'open';
      default:
        return null;
    }
  });

  watch(
    training,
    (currentTraining) => {
      if (!currentTraining || isTrainingControlType(currentTraining.type)) return;

      toast.showErrorToast({
        title: 'Controle de treinamento indisponível',
        message:
          'O controle de treinamento ainda não está disponível para este tipo de treinamento.',
        type: 'error',
      });
      router.push({ name: TRAINING_LIST_ROUTE_NAME });
    },
    { immediate: true }
  );
</script>

<template>
  <div data-testid="training-control-page" class="flex h-full flex-col gap-6 p-6">
    <h1 data-testid="training-control-page-title" class="text-xl/7.5 font-semibold text-gray-07">
      Controle de Treinamento
    </h1>

    <div class="flex justify-end">
      <ColoredButton
        :text="isGenerating ? 'Gerando PDF...' : 'Imprimir'"
        :icon="PrinterIcon"
        icon-alt="Imprimir"
        :disabled="loading || !isSupportedTraining || isGenerating"
        data-testid="training-control-print-button"
        @click="handlePrint"
      />
    </div>

    <div
      v-if="loading"
      data-testid="training-control-loading"
      class="flex flex-1 items-center justify-center"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <div
      v-else-if="isSupportedTraining && training"
      data-testid="training-control-record"
      class="flex flex-col rounded-2xl border border-gray-02 bg-white-01"
    >
      <div class="border-b border-gray-02 p-6">
        <ModalDocumentInfo
          :document-info="TRAINING_CONTROL_QUALITY_RECORD"
          title="CONTROLE DE TREINAMENTO"
          :logo-url="currentLogoUrl"
          @logo-error="handleLogoError"
        />
      </div>

      <template v-if="layout === 'onboarding-role-change'">
        <TrainingControlTypeSection :type="training.type" />
        <TrainingControlTitleSection :title="training.title" />
        <TrainingControlSchedulingSection
          :scheduling="training.scheduling"
          :trainees="training.trainees"
          :matrix-revision="training.matrixRevision"
        />
        <TrainingControlDocumentsSection :documents="training.documents" />
        <TrainingControlInstructorsSection :instructors="training.instructors" />
        <TrainingControlNotesSection :notes="training.notes" />
      </template>

      <template v-else-if="layout === 'sgq-punctual'">
        <TrainingControlTypeSection :type="training.type" />
        <TrainingControlDocumentsSection :documents="training.documents" />
        <TrainingControlProgrammingSection :scheduling="training.scheduling" />
        <TrainingControlCollaboratorsSection :training-id="training.id" />
        <TrainingControlInstructorsSection :instructors="training.instructors" />
        <TrainingControlNotesSection :notes="training.notes" />
      </template>

      <template v-else-if="layout === 'sgq-homologation'">
        <TrainingControlTypeSection :type="training.type" />
        <TrainingControlDocumentsSection :documents="training.documents" />
        <TrainingControlApprovalSection :approval="training.approval" />
        <TrainingControlCollaboratorsSection :training-id="training.id" />
        <TrainingControlInstructorsSection :instructors="training.instructors" />
        <TrainingControlNotesSection :notes="training.notes" />
      </template>

      <template v-else-if="layout === 'open'">
        <TrainingControlTypeSection :type="training.type" />
        <TrainingControlTitleSection :title="training.title" />
        <TrainingControlProgrammingSection :scheduling="training.scheduling" />
        <TrainingControlCollaboratorsSection :training-id="training.id" />
        <TrainingControlOpenInstructorsSection
          :instructors="training.instructors"
          :instructor-type="training.instructorType"
          :external-instructor-cnpj="training.externalInstructorCnpj"
          :external-instructor-company-name="training.externalInstructorCompanyName"
        />
        <TrainingControlCertificateSection :certificate="training.certificate" />
        <TrainingControlNotesSection :notes="training.notes" />
      </template>
    </div>
  </div>
</template>
